import { Reveal, SectionTag } from "./Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const PACKAGES = [
  {
    name: "Foundation",
    price: "₹20,000 / month",
    summary:
      "We build a professional enquiry-generating system for your agency using consistent social media content and managed Facebook and Instagram advertising.",
    items: [
      {
        term: "Social Media Content",
        plain:
          "Every month we create 10 static posts, 2 carousel posts and 2 reels for your agency. Reels are produced using footage, information and media supplied by you.",
      },
      {
        term: "Meta Ads",
        plain:
          "We run your Facebook and Instagram advertising with 2 ad sets and 4 ad creatives. An ad set is a group of ads shown to a chosen audience; a creative is the actual image, video and text people see.",
      },
      {
        term: "Meta Ads Management & Optimization",
        plain:
          "We set up, monitor and improve your campaigns continuously: adjusting audiences, budgets and creatives based on what the data shows.",
      },
      {
        term: "Lead Generation",
        plain:
          "The practical outcome of the system: genuinely interested people contacting your agency through a form or WhatsApp, instead of just scrolling past your posts.",
      },
      {
        term: "Strategy Calls (2 / Month)",
        plain:
          "Two scheduled conversations each month where we review progress, plan the next steps and answer your questions directly.",
      },
      {
        term: "Monthly Performance Report",
        plain:
          "A simple monthly summary of what happened: what was spent, what response came in, and what we are improving next.",
      },
    ],
    takeaway:
      "Best for agencies establishing a professional acquisition system: consistent content, managed advertising and clear reporting.",
    testid: "foundation",
  },
  {
    name: "Growth",
    price: "₹40,000 / month",
    summary:
      "Everything in Foundation, with higher content volume, more advertising variations, and additional channels to capture and follow up with potential customers.",
    items: [
      {
        term: "Social Media Content",
        plain:
          "20 static posts, 4 carousels and 6 reels every month, produced using footage, information and media supplied by you.",
      },
      {
        term: "Meta Ads",
        plain:
          "4 ad sets and 12 ad creatives on Facebook and Instagram, so we can test more audiences and messages at the same time.",
      },
      {
        term: "Meta Ads Management & Optimization",
        plain:
          "We manage campaigns daily: shifting budget toward the audiences and creatives producing real enquiries.",
      },
      {
        term: "Google Ads",
        plain:
          "We help your agency appear when people actively search on Google for Hajj and Umrah packages in your markets.",
      },
      {
        term: "Creative Strategy",
        plain:
          "We plan different advertising messages, offers and creative approaches so we can identify what gets the strongest response from your audience.",
      },
      {
        term: "Retargeting",
        plain:
          "Showing advertisements again to people who previously interacted with your agency, visited your website or saw your ads but did not enquire.",
      },
      {
        term: "Landing Page Recommendations",
        plain:
          "We recommend changes to the page people see after clicking an advertisement so it is clearer, more trustworthy and easier for them to enquire.",
      },
      {
        term: "WhatsApp Lead Flow",
        plain:
          "The structured path that moves an interested person from an advertisement or form into a direct WhatsApp conversation with your team.",
      },
      {
        term: "Email Nurture Strategy",
        plain:
          "A plan for following up with interested people through email when they are not ready to book immediately, keeping your agency present while they decide.",
      },
      {
        term: "Performance Optimization",
        plain:
          "The ongoing work of reviewing results and improving the parts of the system that are underperforming.",
      },
      {
        term: "Strategy Calls & Monthly Report",
        plain:
          "Two strategy calls each month plus a monthly performance report explaining what happened and what comes next.",
      },
    ],
    takeaway:
      "Best for agencies that are already investing in lead generation and want to capture, follow up with and convert more potential customers.",
    testid: "growth",
  },
  {
    name: "Scale",
    price: "₹60,000 / month",
    summary:
      "The full multi-channel acquisition system: the highest content volume, the most advertising variations, and every channel coordinated together.",
    items: [
      {
        term: "Social Media Content",
        plain:
          "30 static posts, 6 carousels and 10 reels every month, produced using footage, information and media supplied by you.",
      },
      {
        term: "Meta Ads",
        plain:
          "6 ad sets and 15 ad creatives on Facebook and Instagram, giving the widest testing coverage.",
      },
      {
        term: "Meta Ads Management & Optimization",
        plain:
          "Continuous campaign management with deeper testing and faster iteration across audiences and creatives.",
      },
      {
        term: "Multi-Channel Strategy",
        plain:
          "We coordinate multiple marketing channels instead of relying on only one source of enquiries.",
      },
      {
        term: "Google Ads",
        plain:
          "We capture relevant high-intent searches on Google alongside your Meta campaigns.",
      },
      {
        term: "WhatsApp Marketing",
        plain:
          "We structure WhatsApp communication and follow-up so potential customers are handled professionally and quickly.",
      },
      {
        term: "Email Marketing",
        plain:
          "We use email to continue communicating with suitable prospects and previous enquiries where appropriate.",
      },
      {
        term: "Funnel Strategy",
        plain:
          "We map the complete journey from seeing an advertisement to becoming an enquiry, having a conversation and reaching a booking opportunity, then strengthen each step.",
      },
      {
        term: "Creative Testing",
        plain:
          "We test different advertising messages, visuals and approaches against each other to identify what performs better, then invest in the winners.",
      },
      {
        term: "Performance Optimization",
        plain:
          "We continuously analyze results and improve the parts of the marketing system that need attention.",
      },
      {
        term: "Strategic Reporting",
        plain:
          "Higher-level reporting that explains not only what happened, but what should be improved or tested next.",
      },
      {
        term: "Strategy Calls (4 / Month)",
        plain:
          "Four scheduled conversations each month for closer coordination, especially useful during busy seasons.",
      },
    ],
    takeaway:
      "Best for established agencies that want a broader, more coordinated marketing system rather than relying on a single advertising channel.",
    testid: "scale",
  },
];

export const PackageExplainer = () => (
  <section className="bg-white py-24 md:py-32 border-t border-charcoal/10" data-testid="package-explainer">
    <div className="max-w-5xl mx-auto px-6">
      <Reveal className="mb-14 max-w-3xl">
        <SectionTag className="mb-6">Simple Explanations</SectionTag>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
          What Each Package Actually Means for Your Agency
        </h2>
        <p className="mt-5 text-base md:text-lg text-charcoal/65 leading-relaxed">
          Marketing terms can get confusing. Here's a simple breakdown of what
          we actually do for you at each level.
        </p>
      </Reveal>

      <Reveal>
        <Accordion type="single" collapsible className="w-full">
          {PACKAGES.map((pkg) => (
            <AccordionItem
              key={pkg.name}
              value={pkg.name.toLowerCase()}
              className="border border-charcoal/10 bg-cream mb-4 px-6 md:px-10 data-[state=open]:border-gold/50"
              data-testid={`explainer-${pkg.testid}`}
            >
              <AccordionTrigger
                className="hover:no-underline py-7 md:py-8 gap-6"
                data-testid={`explainer-trigger-${pkg.testid}`}
              >
                <span className="flex-1 text-left">
                  <span className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <span className="text-2xl md:text-3xl font-bold tracking-tight text-charcoal">
                      {pkg.name}
                    </span>
                    <span className="text-sm font-semibold text-tan">
                      {pkg.price}
                    </span>
                  </span>
                  <span className="mt-2 block text-sm md:text-base text-charcoal/60 font-normal leading-relaxed max-w-2xl">
                    {pkg.summary}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-8 md:pb-10">
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-6 pt-2">
                  {pkg.items.map((item) => (
                    <div key={item.term} data-testid={`explainer-item-${pkg.testid}-${item.term.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <div className="flex items-center gap-2.5">
                        <span className="h-1.5 w-1.5 rotate-45 bg-gold shrink-0" />
                        <span className="text-sm font-semibold tracking-tight text-charcoal">
                          {item.term}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-charcoal/60 leading-relaxed pl-4">
                        {item.plain}
                      </p>
                    </div>
                  ))}
                </div>
                <div
                  className="mt-8 bg-tan px-6 py-5"
                  data-testid={`explainer-takeaway-${pkg.testid}`}
                >
                  <p className="text-sm md:text-base font-semibold text-cream leading-relaxed">
                    {pkg.takeaway}
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);
